import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { Plus, Trash2, Layers } from "lucide-react";

interface Field {
  key: string;
  label: string;
  type?: "text" | "number";
}

interface DynamicListProps {
  label: string;
  subtitle?: string;
  items: any[];
  onChange: (items: any[]) => void;
  fields: Field[];
  indianFormatNumberFields?: string[];
  readOnly?: boolean;
  footer?: React.ReactNode;
}

const DynamicList = ({
  label,
  subtitle,
  items,
  onChange,
  fields,
  indianFormatNumberFields = [],
  readOnly = false,
  footer,
}: DynamicListProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";
  const shouldIndianFormat = (fieldKey: string) =>
    indianFormatNumberFields.includes(fieldKey);
  const formatIndianNumberInput = (value: unknown) => {
    const raw = String(value ?? "").replace(/,/g, "");
    if (!raw || raw === "-" || raw === "." || raw === "-.") {
      return raw;
    }

    const [intPart, decimalPart] = raw.split(".");
    const isNegative = intPart.startsWith("-");
    const absInt = intPart.replace("-", "");
    if (!/^\d+$/.test(absInt)) {
      return String(value ?? "");
    }

    const grouped = Number(absInt).toLocaleString("en-IN");
    const withSign = isNegative ? `-${grouped}` : grouped;
    return decimalPart !== undefined ? `${withSign}.${decimalPart}` : withSign;
  };
  const addItem = () => onChange([...items, {}]);
  const removeItem = (index: number) =>
    onChange(items.filter((_: any, i: number) => i !== index));
  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange(newItems);
  };

  return (
    <Card className="mb-6 overflow-hidden border-slate-200 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between border-b bg-slate-50/80 px-4 py-3">
        <div>
          <CardTitle className="text-sm font-bold text-slate-700">
            {label}
          </CardTitle>
          <p className="text-[10px] font-medium text-slate-500">
            {subtitle ?? "Add and manage list items with structured fields"}
          </p>
        </div>
        {!readOnly && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addItem}
            className="h-8 gap-1.5 border-slate-200 bg-white text-xs shadow-sm transition-all hover:border-primary/20 hover:bg-primary/5 hover:text-primary"
          >
            <Plus className="h-3.5 w-3.5" /> Add New Row
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b bg-slate-50/30">
                {fields.map((f: any) => (
                  <th
                    key={f.key}
                    className="px-4 py-2.5 text-left text-[9px] font-bold uppercase tracking-wider text-slate-500"
                  >
                    {f.label}
                  </th>
                ))}
                {!readOnly && <th className="w-10" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item: any, idx: number) => (
                <tr
                  key={`item-${JSON.stringify(item)}`}
                  className="group transition-colors hover:bg-primary/5"
                >
                  {fields.map((f: any) => (
                    <td key={f.key} className="p-2 px-4">
                      <Input
                        value={
                          f.type === "number" && shouldIndianFormat(f.key)
                            ? formatIndianNumberInput(item[f.key])
                            : (item[f.key] ?? "")
                        }
                        onChange={e => {
                          if (f.type !== "number") {
                            updateItem(idx, f.key, e.target.value);
                            return;
                          }

                          const normalized = shouldIndianFormat(f.key)
                            ? e.target.value.replace(/,/g, "")
                            : e.target.value;
                          if (
                            normalized !== "" &&
                            normalized !== "-" &&
                            normalized !== "." &&
                            !/^-?\d*\.?\d*$/.test(normalized)
                          ) {
                            return;
                          }

                          updateItem(
                            idx,
                            f.key,
                            normalized === "" ? "" : Number(normalized)
                          );
                        }}
                        disabled={readOnly}
                        type={f.type === "number" ? "text" : (f.type ?? "text")}
                        inputMode={f.type === "number" ? "decimal" : undefined}
                        placeholder={`Enter ${f.label}`}
                        className={`h-9 border-slate-200 text-xs transition-all placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:border-primary/50 focus:ring-primary/10 ${readOnlyClass}`}
                      />
                    </td>
                  ))}
                  {!readOnly && (
                    <td className="p-2 text-center">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(idx)}
                        className="h-8 w-8 rounded-full text-slate-400 transition-all hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={fields.length + 1}
                    className="bg-slate-50/20 py-12 text-center text-slate-400"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <Layers className="h-8 w-8 opacity-20" />
                      <span className="italic">
                        No entries yet. Click &quot;Add New Row&quot; to start
                        adding details.
                      </span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
      {footer && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-2.5">
          {footer}
        </div>
      )}
    </Card>
  );
};

export default DynamicList;
